import {
  AudioLines,
  File,
  FileText,
  Mail,
  MessageSquare,
  PaperclipIcon,
} from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import { ArtifactType } from '@/services/types/artifact-digest-types';

interface ArtifactTypeBadgeProps {
  artifactType: ArtifactType;
  size?: 'sm' | 'md';
}

// Helper function to get badge variant and icon based on artifact type
export const getArtifactTypeInfo = (
  type: ArtifactType,
): {
  color: string;
  icon: React.ReactNode;
  label: string;
} => {
  switch (type) {
    case ArtifactType.TRANSCRIPT:
      return {
        color: 'text-blue-500 bg-blue-50',
        icon: <FileText className="h-full w-full" />,
        label: 'Transcript',
      };
    case ArtifactType.AUDIO_CONVERSATION:
      return {
        color: 'text-purple-500 bg-purple-50',
        icon: <AudioLines className="h-full w-full" />,
        label: 'Audio',
      };
    case ArtifactType.DOCUMENT:
      return {
        color: 'text-gray-500 bg-gray-50',
        icon: <FileText className="h-full w-full" />,
        label: 'Document',
      };
    case ArtifactType.EMAIL:
      return {
        color: 'text-amber-500 bg-amber-50',
        icon: <Mail className="h-full w-full" />,
        label: 'Email',
      };
    case ArtifactType.EMAIL_ATTACHMENT:
      return {
        color: 'text-amber-500 bg-amber-50',
        icon: <PaperclipIcon className="h-full w-full" />,
        label: 'Email Attachment',
      };
    case ArtifactType.INTAKE_NOTE:
      return {
        color: 'text-emerald-500 bg-emerald-50',
        icon: <FileText className="h-full w-full" />,
        label: 'Intake Note',
      };
    case ArtifactType.MESSAGE:
      return {
        color: 'text-indigo-500 bg-indigo-50',
        icon: <MessageSquare className="h-full w-full" />,
        label: 'Message',
      };
    case ArtifactType.MESSAGE_ATTACHMENT:
      return {
        color: 'text-indigo-500 bg-indigo-50',
        icon: <PaperclipIcon className="h-full w-full" />,
        label: 'Message Attachment',
      };
    case ArtifactType.CONVERSATION:
      return {
        color: 'text-indigo-500 bg-indigo-50',
        icon: <MessageSquare className="h-full w-full" />,
        label: 'Conversation',
      };
    case ArtifactType.BLOB:
      return {
        color: 'text-gray-500 bg-gray-50',
        icon: <File className="h-full w-full" />,
        label: 'File',
      };
    default:
      return {
        color: 'text-gray-500 bg-gray-50',
        icon: <File className="h-full w-full" />,
        label: 'File',
      };
  }
};

// Component to render a minimalist icon badge for a given artifact type
export const ArtifactTypeBadge: React.FC<ArtifactTypeBadgeProps> = ({
  artifactType,
  size = 'sm',
}) => {
  const { color, icon, label } = getArtifactTypeInfo(artifactType);
  const dimensions = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const iconSizeStyle = size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3';

  return (
    <div className="flex items-center gap-1" title={label}>
      <div
        className={cn(
          'rounded-full flex items-center justify-center shrink-0',
          dimensions,
          color,
        )}
      >
        <div className={iconSizeStyle}>{icon}</div>
      </div>
      {(size === 'md' || size === 'sm') && (
        <span
          className={cn(
            'text-muted-foreground',
            size === 'md' ? 'text-xs' : 'text-[10px] font-medium',
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
};
